import { useState, type ReactNode } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ConfirmPanelProps {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /**
   * When set, the action stays disabled until the user types this exact text.
   * Used for irreversible operations - retyping a tenant code is a deliberate
   * speed bump, not decoration.
   */
  confirmationText?: string;
  confirmationLabel?: string;
  destructive?: boolean;
  /**
   * The action cannot succeed, so the button is disabled rather than offered.
   *
   * For the cases the server will certainly refuse - deleting something still
   * in use, finalising a payroll with no payslips. The description says why;
   * letting the button be pressed anyway only turns an explanation already on
   * screen into an error toast.
   */
  blocked?: boolean;
}

/**
 * Inline confirmation, rendered in place or inside a drawer.
 *
 * WinDocs uses no modal dialogs, so destructive actions confirm here rather
 * than in a popup - the user keeps the surrounding context visible while
 * deciding.
 */
export function ConfirmPanel({
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
  loading = false,
  confirmationText,
  confirmationLabel,
  destructive = true,
  blocked = false,
}: ConfirmPanelProps) {
  const [typed, setTyped] = useState('');

  const matches =
    !confirmationText || typed.trim().toUpperCase() === confirmationText.trim().toUpperCase();

  return (
    <div className="space-y-3">
      <Alert tone={destructive ? 'danger' : 'warning'} title={title}>
        {description}
      </Alert>

      {confirmationText && (
        <div>
          <Label htmlFor="confirm-text">
            {confirmationLabel ?? `Type ${confirmationText} to confirm`}
          </Label>
          <Input
            id="confirm-text"
            value={typed}
            autoComplete="off"
            placeholder={confirmationText}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" size="md" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant={destructive ? 'danger' : 'primary'}
          size="md"
          onClick={onConfirm}
          loading={loading}
          disabled={!matches || blocked}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
